import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../core/firebase';
import {
  ShieldCheck, Award, TrendingUp, ChevronLeft, Globe, Share2,
  Lock, AlertTriangle, Linkedin, Activity, Info,
  Zap, RefreshCw, Send, Trophy, FileText, Shield, List, Check, Copy, X, Loader2
} from 'lucide-react';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from 'recharts';
import CryptoBadge from '../../components/CryptoBadge';
import { useAppContext } from '../../core/App';
import { READINESS_TIERS, VERIFIED_TRAITS } from '../../core/constants/telemetry';
import { COMPLIANCE_FRAMEWORKS } from '../pro-module/data/rewards';
import { db, auth } from '../../core/firebase'; 
import { doc, updateDoc, collection, query, orderBy, onSnapshot } from 'firebase/firestore'; 
import TelemetryCard from '../../components/shared/TelemetryCard'; 
import { useTelemetry } from '../../core/hooks/useTelemetry';

function DifficultySelector() {
  const { currentDifficultyTier, setCurrentDifficultyTier } = useAppContext();
  
  const tiers = [
    { id: 'modern', label: 'Modern', sub: '1x Rewards', desc: 'Standard AI support & autosave.' },
    { id: 'legacy', label: 'Legacy', sub: '3x Rewards', desc: 'Simulated lag & manual saving.' },
    { id: 'brutalist', label: 'Brutalist', sub: '10x Rewards', desc: 'Permadeath & no-cheat protocols.' }
  ];

  return (
    <div className="mt-6 bg-slate-900 rounded-2xl p-6 border border-slate-700 shadow-xl">
      <h3 className="text-white font-black uppercase text-xs tracking-widest mb-4 flex items-center gap-2">
        <Zap size={14} className="text-yellow-400" /> Cognitive Endurance Tier
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {tiers.map((tier) => (
          <button
            key={tier.id}
            onClick={() => typeof setCurrentDifficultyTier === 'function' && setCurrentDifficultyTier(tier.id)}
            className={`p-4 rounded-xl border-2 text-left transition-all ${
              currentDifficultyTier === tier.id 
                ? 'bg-blue-600 border-blue-400 shadow-[0_0_15px_rgba(37,99,235,0.4)]' 
                : 'bg-slate-800 border-slate-700 hover:border-slate-500 opacity-60'
            }`}
          >
            <div className="flex justify-between items-center mb-1">
               <span className="text-sm font-black text-white">{tier.label}</span>
               <span className="text-[10px] font-bold text-blue-200">{tier.sub}</span>
            </div>
            <p className="text-[10px] text-slate-300 leading-snug">{tier.desc}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

function ArchetypeWidget({ archetype, userProfile }) {
  if (!archetype) return null;

  const history = userProfile?.psycap_history || [];
  
  const calcHeroMetric = (base, historyMod) => Math.min(100, Math.max(0, base + historyMod));
  const avgLatency = history.length > 0 ? (history.reduce((acc, entry) => acc + (entry.recoveryLatency || 0), 0) / history.length) : 0;
  
  const heroData = [
      { subject: 'Hope', value: calcHeroMetric(70, history.length * 2) },
      { subject: 'Efficacy', value: calcHeroMetric(65, (archetype.primaryCode === 'Realistic' ? 10 : 0)) },
      { subject: 'Resilience', value: calcHeroMetric(50, (avgLatency > 0 && avgLatency < 5000 ? 20 : -10)) },
      { subject: 'Optimism', value: calcHeroMetric(80, (archetype.secondaryCode === 'Social' ? 5 : 0)) },
  ];

  return (
    <div className="mt-8 bg-slate-900 rounded-2xl p-6 text-white relative overflow-hidden shadow-xl border border-slate-700">
        <div className="relative z-10 flex flex-col md:flex-row gap-8">
            <div className="flex-1">
                {/* Rebranded from "PsyCap Telemetry (PRO)" to remove B2C implications */}
                <div className="flex items-center gap-2 text-emerald-400 font-bold uppercase text-xs tracking-widest mb-2">
                    <Activity size={14} /> PsyCap Telemetry
                </div>
                <h2 className="text-2xl font-black mb-1">{archetype.title}</h2>
                <p className="text-emerald-200 text-sm mb-6 border-l-2 border-emerald-500 pl-3">
                    Continuous monitoring enabled. Mapping behavioral velocity against the HERO Framework.
                </p>
                
                {/* STRATEGIC M&A PATCH: Removed the red "Server Diagnostic Payload" warning box. 
                    Predictive risk flags belong strictly in the Employer Portal. */}
                
                <div className="bg-black/30 p-4 rounded-xl border border-white/10">
                    <p className="text-[10px] text-slate-400 font-black uppercase tracking-wider mb-1 flex items-center gap-1.5">
                        <Info size={12} /> Trait Configuration
                    </p>
                    <p className="text-sm text-slate-200 leading-relaxed font-mono">
                        Dominant Vectors: [{archetype.primaryCode}], [{archetype.secondaryCode}]
                    </p>
                </div>
            </div>
            
            <div className="w-full md:w-64 h-64 bg-slate-950 rounded-xl border border-slate-800 p-2 shadow-inner">
                <ResponsiveContainer width="100%" height="100%">
                    <RadarChart cx="50%" cy="50%" outerRadius="70%" data={heroData}>
                        <PolarGrid stroke="#334155" />
                        <PolarAngleAxis dataKey="subject" tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 'bold' }} />
                        <PolarRadiusAxis angle={30} domain={[0, 100]} tick={false} axisLine={false} />
                        <Radar name="PsyCap" dataKey="value" stroke="#10b981" fill="#10b981" fillOpacity={0.4} />
                    </RadarChart>
                </ResponsiveContainer>
            </div>
        </div>
    </div>
  );
}

export default function MyCareerPathScreen({ pro101Progress }) { 
  const { userProfile, goToDashboard, setToastMessage, handleNavigate, setUserProfile, authUser } = useAppContext();
  const { vciScore, readinessTier } = useTelemetry();
  
  const [activeTab, setActiveTab] = useState('trophies');
  const [copiedId, setCopiedId] = useState(null);
  const [selectedBrain, setSelectedBrain] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  const [blindPings, setBlindPings] = useState([]);

  useEffect(() => {
    if ('Notification' in window && Notification.permission !== 'granted' && Notification.permission !== 'denied') {
      Notification.requestPermission();
    }
  }, []);

  useEffect(() => {
    if (!authUser) return;
    const pingsRef = collection(db, 'users', authUser.uid, 'blind_pings');
    const q = query(pingsRef, orderBy('timestamp', 'desc'));
    
    const unsub = onSnapshot(q, (snap) => {
      const pings = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      
      snap.docChanges().forEach((change) => {
          if (change.type === 'added') {
              const ping = change.doc.data();
              if (ping.status === 'pending' && 'Notification' in window && Notification.permission === 'granted') {
                  new Notification("An Enterprise Recruiter has requested your Verified Provenance Profile. Click to review.");
              }
          }
      });

      setBlindPings(pings);
    });
    
    return () => unsub();
  }, [authUser]);

  const handleAcceptPing = async (pingId) => {
    try {
      await updateDoc(doc(db, 'users', authUser.uid, 'blind_pings', pingId), {
        status: 'accepted',
        acceptedAt: Date.now()
      });
      if (typeof setToastMessage === 'function') {
        setToastMessage({ type: 'success', text: "Connection Accepted! Your profile has been revealed to the recruiter." });
      }
    } catch (err) {
      console.error("Failed to accept ping:", err);
    }
  };

  const handleDeclinePing = async (pingId) => {
    try {
      await updateDoc(doc(db, 'users', authUser.uid, 'blind_pings', pingId), {
        status: 'declined',
        declinedAt: Date.now()
      });
      if (typeof setToastMessage === 'function') {
        setToastMessage({ type: 'success', text: "Request declined. Your identity remains secure." });
      }
    } catch (err) {
      console.error("Failed to decline ping:", err);
    }
  };

  const currentLevelInfo = READINESS_TIERS.find(l => l.name === readinessTier) || READINESS_TIERS[0];
  const nextLevelInfo = READINESS_TIERS.find(l => l.tier === currentLevelInfo.tier + 1);

  let progressPercent = 0;
  const vciForThisLevel = vciScore - currentLevelInfo.minVCI;
  if (nextLevelInfo) {
    const vciNeededForNextLevel = nextLevelInfo.minVCI - currentLevelInfo.minVCI;
    progressPercent = Math.max(0, Math.min(100, (vciForThisLevel / vciNeededForNextLevel) * 100));
  } else {
    progressPercent = 100;
  }

  const earnedBadgeIds = new Set(userProfile?.verifiedCompetencies?.length > 0 ? userProfile.verifiedCompetencies : (userProfile?.badges || []));
  const allBadges = Object.keys(VERIFIED_TRAITS).map(id => ({ id, ...VERIFIED_TRAITS[id] }));
  const unlockedGameIds = pro101Progress ? Object.keys(pro101Progress).filter(key => pro101Progress[key] === true) : [];

  const handleCopy = (text, id) => { navigator.clipboard.writeText(text); setCopiedId(id); setTimeout(() => setCopiedId(null), 2000); };

  const handleChangeTrack = async () => {
    if (!window.confirm(`Are you sure? Changing your Career Track will reset your specialized recommendations.`)) return;

    try {
        const userRef = doc(db, 'users', authUser.uid);
        await updateDoc(userRef, {
            careerTrack: null 
        });
        if (typeof setUserProfile === 'function') {
            setUserProfile(prev => ({ ...prev, careerTrack: null }));
        }
        if (typeof handleNavigate === 'function') {
            handleNavigate('career-track');
        }
    } catch (e) {
        console.error("Track switch failed:", e);
        alert("Failed to switch track. Please try again.");
    }
  };

  const handleSignOut = async () => {
    window.localStorage.clear();
    await auth.signOut();
  };

  const handleDeleteAccount = async () => {
      const confirmDelete = window.confirm(
          "⚠️ DANGER: This will permanently delete your account and all associated data. This action cannot be undone. Are you absolutely sure?"
      );
      
      if (!confirmDelete) return;

      setIsDeleting(true);
      try {
          const deleteUserAccountFn = httpsCallable(functions, 'deleteUserAccount');
          await deleteUserAccountFn();
          alert("Your account and data have been successfully deleted.");
          window.localStorage.clear();
          await auth.signOut();
      } catch (err) {
          console.error("Delete Account Error:", err);
          alert(`Failed to delete account: ${err.message}. Please try again later.`);
          setIsDeleting(false);
      }
  };

  return (
    <div className="max-w-4xl mx-auto animate-fade-in pb-12 pt-6 px-4 sm:px-0">
      
      {/* CRITICAL PATCH: Safely invoking callbacks via typeof checks */}
      <button onClick={() => typeof goToDashboard === 'function' ? goToDashboard() : null} className="flex items-center text-gray-600 font-medium hover:text-blue-600 transition-colors mb-6">
        <ChevronLeft className="w-5 h-5 mr-1" /> Back to Dashboard
      </button>

      <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 mb-8">
        <div className="h-32 w-full bg-gradient-to-r from-slate-800 to-slate-900"></div>

        <div className="px-6 sm:px-8 pb-8">
          <div className="flex flex-col sm:flex-row items-center sm:items-end -mt-12 mb-6">
             <div className="relative group -mt-16 sm:-mt-24 z-10">
                <TelemetryCard 
                    levelName={readinessTier} 
                    vciScore={vciScore || 0} 
                    // PIVOT: Route to the enterprise passport instead of the archetype engine
                    onClick={() => typeof handleNavigate === 'function' ? handleNavigate('passport-hidden') : null} 
                />
             </div>

            <div className="mt-4 sm:mt-0 sm:ml-6 text-center sm:text-left flex-1">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{readinessTier}</h1>
              <div className="flex items-center justify-center sm:justify-start gap-2 mt-1">
                  <p className="text-gray-500 font-medium">{userProfile.careerTrack || "Undecided"} Track</p>
                  <button 
                    onClick={handleChangeTrack}
                    className="text-[10px] flex items-center gap-1 bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 px-2 py-1 rounded border border-gray-200 transition-colors"
                  >
                    <RefreshCw size={10} /> Switch
                  </button>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 mt-6">
             <div className="flex justify-between text-sm font-medium text-gray-600 mb-2">
                <span>Current VCI: {vciScore}</span>
                <span>Next Tier: {nextLevelInfo ? nextLevelInfo.minVCI : 'MAX'}</span>
             </div>
             <div className="h-4 w-full bg-gray-200 rounded-full overflow-hidden border border-gray-300">
                <div className="h-full bg-blue-500 transition-all duration-1000 ease-out relative" style={{ width: `${progressPercent}%` }}>
                  <div className="absolute inset-0 bg-white opacity-20" style={{ backgroundImage: 'linear-gradient(45deg,rgba(255,255,255,.15) 25%,transparent 25%,transparent 50%,rgba(255,255,255,.15) 50%,rgba(255,255,255,.15) 75%,transparent 75%,transparent)', backgroundSize: '1rem 1rem' }}></div>
                </div>
             </div>
             <p className="text-xs text-gray-400 mt-2 text-right">{nextLevelInfo ? `${Math.ceil(nextLevelInfo.minVCI - vciScore)} VCI to go` : "You are at the top!"}</p>
          </div>

          <DifficultySelector />
          <ArchetypeWidget archetype={userProfile.careerArchetype} userProfile={userProfile} />
          
        </div>
      </div>

      {/* --- ZERO-KNOWLEDGE CONNECTION REQUESTS --- */}
      {blindPings.length > 0 && (
        <div className="mb-8 space-y-3 animate-fade-in">
          <h3 className="text-lg font-black text-slate-800 flex items-center gap-2">
            <Send size={20} className="text-indigo-500" /> Enterprise Connection Requests
          </h3>
          {blindPings.map(ping => (
             <div key={ping.id} className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-sm">
                <div>
                   <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest ${ping.status === 'pending' ? 'bg-indigo-200 text-indigo-800' : ping.status === 'accepted' ? 'bg-emerald-200 text-emerald-800' : 'bg-slate-200 text-slate-800'}`}>
                          {ping.status}
                      </span>
                      <span className="text-xs text-slate-500 font-mono">
                          {ping.timestamp ? new Date(ping.timestamp?.toDate ? ping.timestamp.toDate() : ping.timestamp).toLocaleDateString() : 'Just now'}
                      </span>
                   </div>
                   <p className="text-sm font-medium text-slate-800">"{ping.message}"</p>
                   {ping.status === 'pending' && <p className="text-xs text-slate-500 mt-1 flex items-center gap-1"><Lock size={10}/> Your PII is currently hidden.</p>}
                   {ping.status === 'accepted' && <p className="text-xs text-emerald-600 font-bold mt-1 flex items-center gap-1"><ShieldCheck size={10}/> Contact info revealed to partner.</p>}
                </div>
                {ping.status === 'pending' && (
                    <div className="flex gap-2 w-full sm:w-auto">
                       <button onClick={() => handleDeclinePing(ping.id)} className="flex-1 sm:flex-none px-4 py-2 bg-white text-slate-600 border border-slate-200 hover:bg-slate-50 rounded-lg text-xs font-bold transition shadow-sm">Decline</button>
                       <button onClick={() => handleAcceptPing(ping.id)} className="flex-1 sm:flex-none px-4 py-2 bg-indigo-600 text-white rounded-lg text-xs font-bold shadow-md hover:bg-indigo-700 transition">Accept & Reveal</button>
                    </div>
                )}
             </div>
          ))}
        </div>
      )}

      <div className="flex border-b border-gray-200 mb-6">
        <button onClick={() => setActiveTab('trophies')} className={`pb-4 px-6 text-lg font-bold transition-colors flex items-center gap-2 ${activeTab === 'trophies' ? 'border-b-2 border-yellow-500 text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
          <Trophy className={activeTab === 'trophies' ? 'text-yellow-500' : 'text-gray-400'} size={20} /> Verified Competencies
        </button>
        <button onClick={() => setActiveTab('brains')} className={`pb-4 px-6 text-lg font-bold transition-colors flex items-center gap-2 ${activeTab === 'brains' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}>
          <FileText className={activeTab === 'brains' ? 'text-blue-500' : 'text-gray-400'} size={20} /> Compliance Frameworks
        </button>
      </div>

      {activeTab === 'trophies' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 animate-in fade-in slide-in-from-bottom-2">
          {allBadges.map((badge) => {
            const isUnlocked = earnedBadgeIds.has(badge.id);
            const Icon = badge.icon;
            return (
              <div key={badge.id} className={`relative p-5 rounded-xl border transition-all duration-300 ${isUnlocked ? 'bg-white border-green-200 shadow-md transform hover:-translate-y-1' : 'bg-gray-50 border-gray-200 opacity-70'}`}>
                <div className="flex justify-between items-start mb-3">
                  <div className={`p-3 rounded-full ${isUnlocked ? 'bg-green-100 text-green-600' : 'bg-gray-200 text-gray-400'}`}><Icon className="w-6 h-6" /></div>
                  {isUnlocked ? <Shield className="w-4 h-4 text-green-500" /> : <Lock className="w-4 h-4 text-gray-400" />}
                </div>
                <h3 className={`font-bold ${isUnlocked ? 'text-gray-900' : 'text-gray-500'}`}>{badge.name}</h3>
                <p className="text-xs text-gray-500 mt-1 leading-snug">{badge.description}</p>
              </div>
            );
          })}
        </div>
      )}

      {activeTab === 'brains' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 animate-in fade-in slide-in-from-bottom-2">
          {COMPLIANCE_FRAMEWORKS.map((item) => {
            const isUnlocked = unlockedGameIds.includes(item.linkedGameId);
            return (
              <div key={item.id} className={`group relative border rounded-xl p-5 flex flex-col transition-all ${isUnlocked ? 'bg-white border-gray-200 hover:shadow-lg hover:border-blue-200' : 'bg-gray-50 border-gray-100 opacity-70'}`}>
                {!isUnlocked && <div className="absolute inset-0 z-10 bg-gray-100/50 backdrop-blur-[1px] flex items-center justify-center rounded-xl"><div className="bg-white p-2 rounded-full shadow-sm"><Lock className="w-5 h-5 text-gray-400" /></div></div>}
                <div className="flex justify-between items-start mb-3">
                  <div className={`p-2 rounded-lg ${item.type === 'copy_paste' ? 'bg-blue-50 text-blue-600' : 'bg-purple-50 text-purple-600'}`}>{item.type === 'copy_paste' ? <FileText size={18} /> : <List size={18} />}</div>
                  <span className="text-[10px] uppercase font-bold text-gray-400 tracking-wider">{item.category || 'General'}</span>
                </div>
                <h3 className="font-bold text-gray-800 mb-1 leading-tight">{item.title}</h3>
                <p className="text-xs text-gray-500 mb-4 line-clamp-2">{item.description}</p>
                <div className="mt-auto pt-4 border-t border-gray-100">
                  {item.type === 'copy_paste' ? (
                    <button disabled={!isUnlocked} onClick={() => handleCopy(item.content, item.id)} className="w-full py-2 flex items-center justify-center gap-2 bg-gray-50 hover:bg-blue-50 text-gray-600 hover:text-blue-600 rounded-lg text-xs font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                      {copiedId === item.id ? <><Check size={14} /> Copied!</> : <><Copy size={14} /> Copy Template</>}
                    </button>
                  ) : (
                    <button disabled={!isUnlocked} onClick={() => setSelectedBrain(item)} className="w-full py-2 flex items-center justify-center gap-2 bg-gray-50 hover:bg-purple-50 text-gray-600 hover:text-purple-600 rounded-lg text-xs font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                      <List size={14} /> View Playbook
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selectedBrain && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedBrain(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200" onClick={e => e.stopPropagation()}>
            <div className="bg-purple-600 p-6 flex justify-between items-start">
              <div><h2 className="text-white font-bold text-xl">{selectedBrain.title}</h2><p className="text-purple-200 text-sm mt-1">{selectedBrain.category}</p></div>
              <button onClick={() => setSelectedBrain(null)} className="text-white/80 hover:text-white hover:bg-white/20 rounded-full p-1 transition-colors"><X size={20} /></button>
            </div>
            <div className="p-6">
              <div className="space-y-3">
                {Array.isArray(selectedBrain.content) ? (
                  selectedBrain.content.map((line, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
                      <div className="mt-1 min-w-[20px]"><div className="w-5 h-5 rounded border-2 border-purple-200 flex items-center justify-center"><div className="w-2.5 h-2.5 rounded-sm bg-purple-600" /></div></div>
                      <p className="text-gray-700 text-sm font-medium">{line}</p>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-700 whitespace-pre-line">{selectedBrain.content}</p>
                )}
              </div>
              <button onClick={() => setSelectedBrain(null)} className="w-full mt-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold rounded-xl transition-colors">Close Playbook</button>
            </div>
          </div>
        </div>
      )}

      {/* --- PURGE PROTOCOL (Sign Out & Deletion) --- */}
      <div className="mt-12 text-center pt-8 border-t border-gray-200 flex flex-col items-center justify-center gap-4">
         <button 
            onClick={handleSignOut} 
            className="px-8 py-3 bg-slate-50 text-slate-600 font-bold rounded-xl border border-slate-200 hover:bg-slate-100 transition-colors"
         >
            Terminate Session
         </button>
         
         <button 
            onClick={handleDeleteAccount}
            disabled={isDeleting}
            className="px-4 py-2 flex items-center gap-2 text-xs font-bold text-red-500 hover:text-red-700 hover:underline transition-colors disabled:opacity-50"
         >
            {isDeleting ? <Loader2 size={12} className="animate-spin" /> : <AlertTriangle size={12} />}
            Delete My Account & Data
         </button>
         
         <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1">
            Data erasure complies with CCPA / GDPR mandates.
         </p>
      </div>

    </div>
  );
}